import React from 'react';
import {
  IonButton,
  IonContent,
  IonPage,
  useIonToast,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import Header from 'src/components/Header';
import ClimaOrganizacionalAPI from 'src/services/climaOrganizacional';
import './ClimaOrganizacional.css';

type Respuestas = Record<number, number>; // 1-based index -> 1..5

type PreguntaClima = {
  pregunta: number;
  criterio: string;
  texto: string;
};

const OPCIONES = [1, 2, 3, 4, 5] as const;

type LikertProps = {
  value?: number;
  onChange: (v: number) => void;
  ariaLabel?: string;
};

const LikertRow: React.FC<LikertProps> = ({ value, onChange, ariaLabel }) => {
  return (
    <div className="co-likert" role="radiogroup" aria-label={ariaLabel || 'Escala 01 a 05'}>
      {OPCIONES.map((op) => {
        const selected = value === op;
        if (selected) {
          return (
            <button
              key={op}
              type="button"
              className="co-likert-option co-likert-option--selected"
              onClick={() => onChange(op)}
              aria-checked="true"
              role="radio"
            >
              {String(op).padStart(2, '0')}
            </button>
          );
        }
        return (
          <button
            key={op}
            type="button"
            className="co-likert-option"
            onClick={() => onChange(op)}
            aria-checked="false"
            role="radio"
          >
            {String(op).padStart(2, '0')}
          </button>
        );
      })}
    </div>
  );
};

const ClimaOrganizacional: React.FC = () => {
  const history = useHistory();
  const [present] = useIonToast();

  const [tipoFormulario, setTipoFormulario] = React.useState<'OP' | 'ADM' | null>(null);
  const [tituloFormulario, setTituloFormulario] = React.useState('Clima Organizacional');
  const [preguntas, setPreguntas] = React.useState<PreguntaClima[]>([]);
  const [cargandoFormulario, setCargandoFormulario] = React.useState(true);
  const [yaRealizada, setYaRealizada] = React.useState(false);
  const [encuestaId, setEncuestaId] = React.useState<number | null>(null);
  const [respuestas, setRespuestas] = React.useState<Respuestas>({});
  const [comentario, setComentario] = React.useState('');
  const [enviando, setEnviando] = React.useState(false);

  React.useEffect(() => {
    const cargar = async () => {
      try {
        const cuestionarioResp = await ClimaOrganizacionalAPI.obtenerCuestionario();
        const preguntasApi = Array.isArray(cuestionarioResp?.data?.preguntas)
          ? cuestionarioResp.data.preguntas
          : [];

        setPreguntas(preguntasApi);
        setTipoFormulario(cuestionarioResp?.data?.tipo_formulario || null);
        setTituloFormulario(cuestionarioResp?.data?.titulo || 'Clima Organizacional');

        try {
          const estadoResp = await ClimaOrganizacionalAPI.obtenerEstado();
          const ya = Boolean(estadoResp?.data?.ya_realizada);
          setYaRealizada(ya);
          setEncuestaId(
            estadoResp?.data?.encuesta_id !== undefined && estadoResp?.data?.encuesta_id !== null
              ? Number(estadoResp.data.encuesta_id)
              : null
          );

          if (ya) {
            try {
              sessionStorage.setItem('clima_organizacional_realizada_session', '1');
            } catch {}
            present({
              message: 'YA FUE REALIZADA LA EVALUACION DE CLIMA ORGANIZACIONAL',
              duration: 3000,
              color: 'warning',
            });
            history.replace('/home');
            return;
          }
        } catch {
          setYaRealizada(false);
          setEncuestaId(null);
        }
      } catch {
        setTipoFormulario(null);
        setPreguntas([]);
        present({
          message: 'No se pudo cargar el formulario. Intenta nuevamente.',
          duration: 4000,
          color: 'warning',
        });
        history.replace('/');
      } finally {
        setCargandoFormulario(false);
      }
    };

    cargar();
  }, [history, present]);

  const totalRespondidas = Object.keys(respuestas).length;
  const totalPreguntas = preguntas.length;
  const completo = totalPreguntas > 0 && totalRespondidas === totalPreguntas;

  const setRespuesta = (idx: number, valor: number) => {
    setRespuestas((prev) => ({ ...prev, [idx]: valor }));
  };

  const onEnviar = async () => {
    if (yaRealizada) {
      present({
        message: 'YA FUE REALIZADA LA EVALUACION DE CLIMA ORGANIZACIONAL',
        duration: 4000,
        color: 'warning',
      });
      return;
    }

    if (!completo) {
      present({
        message: 'Completá todas las preguntas antes de enviar.',
        duration: 3500,
        color: 'warning',
      });
      return;
    }

    try {
      setEnviando(true);
      const respuestasPayload = preguntas.map((item) => ({
        pregunta: item.pregunta,
        valor: respuestas[item.pregunta],
      }));

      const response = await ClimaOrganizacionalAPI.enviarRespuestas(respuestasPayload, comentario);

      try {
        const key = 'clima_organizacional_ultimo_envio';
        localStorage.setItem(
          key,
          JSON.stringify({
            version: 2,
            fecha: new Date().toISOString(),
            encuesta_id: response?.data?.encuesta_id,
            respuestas: respuestasPayload,
            comentario,
          })
        );
        sessionStorage.setItem('clima_organizacional_realizada_session', '1');
      } catch {}

      present({
        message: 'Respuestas enviadas y guardadas correctamente.',
        duration: 3500,
        color: 'success',
      });

      history.replace('/home');
    } catch (err: any) {
      const apiMessage = err?.response?.data?.error || err?.response?.data?.info;
      const status = err?.response?.status;

      if (status === 409) {
        setYaRealizada(true);
        const encId = err?.response?.data?.encuesta_id;
        setEncuestaId(encId !== undefined && encId !== null ? Number(encId) : null);
        try {
          sessionStorage.setItem('clima_organizacional_realizada_session', '1');
        } catch {}
      }
      present({
        message: typeof apiMessage === 'string' && apiMessage.trim().length ? apiMessage : 'No se pudo enviar. Intenta nuevamente.',
        duration: 4000,
        color: 'danger',
      });
    } finally {
      setEnviando(false);
    }
  };

  if (cargandoFormulario) {
    return (
      <IonPage>
        <Header titulo={tituloFormulario} />
        <IonContent className="ion-padding">
          <div className="text-muted">Cargando...</div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <Header titulo={tituloFormulario} />
      <IonContent className="ion-padding">
        <div className="container">
          {yaRealizada ? (
            <div className="co-done">
              <div className="co-done-title">YA FUE REALIZADA LA EVALUACION DE CLIMA ORGANIZACIONAL {tipoFormulario ? `(${tipoFormulario})` : ''}</div>
              {encuestaId !== null && (
                <div className="text-muted" style={{ marginTop: 8 }}>
                  N° de encuesta: {encuestaId}
                </div>
              )}

              <IonButton expand="block" color="medium" onClick={() => history.replace('/home')} style={{ marginTop: 16 }}>
                Volver
              </IonButton>
            </div>
          ) : (
            <>
              <div className="text-muted">
                Formulario: {tipoFormulario || '-'} · Respondidas: {totalRespondidas}/{totalPreguntas}
              </div>

              <div className="co-scale">
                <div className="co-scale-title">Escala</div>
                <div className="co-scale-lines">
                  <div><strong>05</strong> Totalmente de acuerdo</div>
                  <div><strong>04</strong> De acuerdo</div>
                  <div><strong>03</strong> Ni en desacuerdo ni de acuerdo</div>
                  <div><strong>02</strong> En desacuerdo</div>
                  <div><strong>01</strong> Totalmente en desacuerdo</div>
                </div>
              </div>

              <div className="mt-3">
                {preguntas.map((item) => {
                  const idx = item.pregunta;
                  const value = respuestas[idx];
                  return (
                    <div key={idx} className="mb-3 co-question-card">
                      <div className="co-question-criteria">Criterio: {item.criterio}</div>
                      <div className="co-question-title">
                        {idx}. {item.texto}
                      </div>

                      <LikertRow
                        value={value}
                        onChange={(v) => setRespuesta(idx, v)}
                        ariaLabel={`Pregunta ${idx}: seleccione 01 a 05`}
                      />

                      {!value && <div className="co-required-hint">Seleccioná una opción.</div>}
                    </div>
                  );
                })}
              </div>

              <div className="co-comentario-wrap">
                <div className="co-comentario-label">Comentario (opcional)</div>
                <textarea
                  className="co-comentario-textarea"
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                  placeholder="Escribí aquí cualquier comentario adicional..."
                  rows={6}
                />
              </div>

              <IonButton expand="block" color="primary" disabled={!completo || enviando} onClick={onEnviar}>
                {enviando ? 'Enviando...' : 'Enviar'}
              </IonButton>

              {!completo && (
                <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                  Para enviar, debés responder todas las preguntas del formulario.
                </div>
              )}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ClimaOrganizacional;
